/**
 * @module layoutUtils
 * @description Responsive board configuration calculation and rack scaling logic
 * for the Game component. Determines layout mode (phone, square, tall, tv, hd,
 * rackonly) and computes cell size, sidebar width, and rack dimensions based on
 * the current viewport.
 */

/**
 * @typedef {object} CalcBoardConfigDeps
 * @property {function(number): void} setSidebarWidth - Sets the sidebar panel width in pixels.
 * @property {function(boolean): void} setTooShort - Sets whether the viewport is too short.
 * @property {function(string): void} setTab - Sets the active sidebar tab ("game" | "scoreboard").
 * @property {function(string): void} setBoardConfig - Sets the layout mode string.
 * @property {function(number): void} setBoardScale - Sets the board cell size in pixels.
 * @property {function(number): void} setRackWidth - Sets the rack width in pixels.
 * @property {function(number): void} setRackScale - Sets the rack tile scale factor.
 */

/**
 * Calculates and applies the responsive layout configuration based on the current
 * viewport dimensions. Determines board mode (phone / square / tall / tv / hd / rackonly),
 * board cell size, and sidebar width, then writes the results via the provided state setters.
 * @param {CalcBoardConfigDeps} deps - State setters to apply the computed configuration.
 * @returns {void}
 */
export function calcBoardConfig(deps) {
    const { setSidebarWidth, setTooShort, setTab, setBoardConfig, setBoardScale, setRackWidth, setRackScale } = deps;

    let newBoardConfig = "";
    let aspectRatio =
        window.visualViewport.width / window.visualViewport.height;
    let width = window.visualViewport.width;
    let height = window.visualViewport.height - 56;
    console.log("WINDOW SIZE", width, height, aspectRatio);

    let boardWidth = width;

    if (aspectRatio >= 1.25) {
        // wide mode
        // calculate the rack scale based on the height
        if (width > 960) {
            let boardScale = height / 17.7144; //18.1436;
            boardWidth = boardScale * 16;
            if (aspectRatio > 1.48) {
                newBoardConfig = "hd";
                setSidebarWidth((width - boardWidth) / 2);
            } else {
                newBoardConfig = "tv";
                setSidebarWidth(width - boardWidth);
            }
            setTooShort(false);
            setTab("game");
        } else {
            boardWidth = width;
            setSidebarWidth(0);
            newBoardConfig = "rackonly";
            setRackWidth(width);
            setRackScale((width / 700) * .75);
        }
    } else {
        if (width >= 960 && aspectRatio < 1.25) {
            console.log("TALL SIZE");
            setSidebarWidth(360);
            boardWidth = width - 360;
            newBoardConfig = "tall";
        } else if (width < 960 && aspectRatio < 1.25 && aspectRatio >= .75) {
            console.log("SQUARE?")
            boardWidth = height - 200;
            newBoardConfig = "square"
            if (aspectRatio >= 1) {
                setSidebarWidth(360)
                boardWidth = width - 360;
            } else {
                setSidebarWidth(0)
                boardWidth = height - 250;
            }

        } else if (aspectRatio < 0.66 && width < 500) {
            newBoardConfig = "phone";
            // console.log("PHONE");
            let boardScale = width / 16;
            boardWidth = boardScale * 16;
            setSidebarWidth(width - boardWidth);
        }
        // if too short...
        // if (
        //     aspectRatio < 1 &&
        //     height -
        //         176 -
        //         boardWidth -
        //         ((boardWidth * 75) / 700) * 1.25 -
        //         42 <
        //         0
        // ) {
        //     setTooShort(true);
        //     setTab("board");
        else {
            newBoardConfig = "phone"
            setTooShort(false);
            setTab("game");
        }
    }
    setBoardConfig(newBoardConfig);
    console.log("BOARD SIZE", aspectRatio, width, boardWidth, newBoardConfig);
    setBoardScale(boardWidth / 16);
}

/**
 * @typedef {object} CalcRackScaleDeps
 * @property {function(number): void} setRackWidth - Sets the rack width in pixels.
 * @property {function(number): void} setRackScale - Sets the rack tile scale factor.
 */

/**
 * Calculates and applies the rack tile scale factor.
 * When boardScale is provided, derives the scale from it directly.
 * When omitted, reads the rack element's current width from the DOM.
 * @param {number|undefined} boardScale - The current board scale factor. If omitted, reads from DOM.
 * @param {CalcRackScaleDeps} deps - State setters to apply the computed scale.
 * @returns {void}
 */
export function calcRackScale(boardScale, deps) {
    const { setRackWidth, setRackScale } = deps;
    if (boardScale) {
        console.log("Setting from Board Width", boardScale * 16);
        let newRackWidth = boardScale * 16;
        setRackScale((75 * newRackWidth) / 700);
    } else {
        let boardAndRacks = [...document.querySelectorAll(".boardAndRack")];
        let boardAndRack = boardAndRacks.find(
            (e) => e?.style.dislay != "none"
        );
        console.log("BOARD AND RACK", boardAndRack?.clientWidth);
        let newRackWidth =
            document.querySelector(".rack")?.clientWidth || 0;
        setRackWidth(newRackWidth);
        console.log(
            "RESETTING RACK SIZE",
            newRackWidth,
            (75 * newRackWidth) / 700
        );
        setRackScale((75 * newRackWidth) / 700);
    }
}
